import { useEffect } from "react";

function getSamePageHashLink(event: MouseEvent): HTMLAnchorElement | null {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return null;
  }

  const target = event.target instanceof Element ? event.target : null;
  const link = target?.closest<HTMLAnchorElement>("a[href*='#']");

  if (!link || link.target || link.hasAttribute("download")) {
    return null;
  }

  const url = new URL(link.href, window.location.href);

  if (
    url.origin !== window.location.origin ||
    url.pathname !== window.location.pathname ||
    url.search !== window.location.search ||
    url.hash === ""
  ) {
    return null;
  }

  return link;
}

function getTargetId(hash: string): string {
  const value = hash.replace(/^#/, "");

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function useCleanHashScroll() {
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      const link = getSamePageHashLink(event);

      if (!link) {
        return;
      }

      const target = document.getElementById(
        getTargetId(new URL(link.href).hash),
      );

      if (!target) {
        return;
      }

      event.preventDefault();

      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      target.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });

      window.history.replaceState(
        window.history.state,
        document.title,
        `${window.location.pathname}${window.location.search}`,
      );
    }

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);
}
